import { useEffect, useMemo, useState } from 'react';
import { Search, UserCheck, AlertCircle } from 'lucide-react';
import { teamMembersService, type TeamMember } from '../../../services/team-members.service';
import { UserAvatar } from '../../UserAvatar';

interface TeamSupervisorPickerProps {
  value: string | null;
  onChange: (supervisorId: string | null) => void;
  disabled?: boolean;
}

function displayName(m: TeamMember): string {
  if (m.displayName) return m.displayName;
  if (m.firstName || m.lastName) return `${m.firstName || ''} ${m.lastName || ''}`.trim();
  return m.email;
}

export function TeamSupervisorPicker({ value, onChange, disabled }: TeamSupervisorPickerProps) {
  const [supervisors, setSupervisors] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await teamMembersService.getTeamMembers({
          role: 'supervisor',
          status: 'active',
          limit: 200,
        });
        if (!cancelled) setSupervisors(res.data);
      } catch (e) {
        if (!cancelled) setError('Failed to load supervisors');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return supervisors;
    const q = search.toLowerCase();
    return supervisors.filter(
      (m) =>
        displayName(m).toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [supervisors, search]);

  if (loading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">Loading supervisors...</div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (supervisors.length === 0) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
          <div className="text-sm text-yellow-800 dark:text-yellow-200">
            No supervisors found. Invite a user with the <strong>supervisor</strong> role from
            <strong> Settings → Team Members</strong> first.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search supervisors..."
          disabled={disabled}
          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
        />
      </div>

      <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No matches
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => {
              const selected = value === m.id;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => onChange(selected ? null : m.id)}
                    disabled={disabled}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                      selected
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <UserAvatar
                      src={m.avatarUrl}
                      name={displayName(m)}
                      className="h-8 w-8 flex-shrink-0 bg-blue-100 text-sm font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {displayName(m)}
                      </div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {m.email}
                      </div>
                    </div>
                    {selected && (
                      <UserCheck className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
